/**
 * Gift-fund gate: gift-city status call, then the mail or WhatsApp sheet.
 *
 * Belongs here: ISIN check, loading, sheet choice, failure toast.
 * Does not belong here: the status request, sheet copy, or mailto / WhatsApp details.
 */
import type { ReactElement } from 'react'
import { useBottomSheet } from '../../state-management/contexts/BottomSheetContext'
import { useLoading } from '../../state-management/contexts/LoadingContext'
import { useToast } from '../../state-management/contexts/ToastContext'
import { GiftCityOnboardingStatus, useGiftCityOnboardingStatusQuery } from './giftCityOnboardingStatus'
import { PpfasGiftFundNotOnboardedBottomSheet } from './bottom-sheet/PpfasGiftFundNotOnboardedBottomSheet'
import { PpfasGiftFundOnboardedBottomSheet } from './bottom-sheet/PpfasGiftFundOnboardedBottomSheet'

export const PPFAS_GIFT_FUND_ISIN = 'INPPFASNDQ10'
const PPFAS_GIFT_FUND_ERROR_TOAST = "Can't invest in this fund right now. Please try again."

/** Parag Parikh IFSC Nasdaq 100 FoF — the only ISIN that opens the gift-fund sheet. */
export function isPpfasGiftFundIsin(isin: string): boolean {
  return isin === PPFAS_GIFT_FUND_ISIN
}

/** Opens the gift-fund sheet when status requires it. False means the caller continues the normal invest path. */
export function usePpfasGiftFundHandling(options?: { isRecommendationSheetSource?: boolean }) {
  const { refetch } = useGiftCityOnboardingStatusQuery(PPFAS_GIFT_FUND_ISIN)
  const { showToast } = useToast()
  const { showLoading, hideLoading } = useLoading()
  const {
    openBottomSheet,
    closeBottomSheet,
    openNestedBottomSheet,
    closeNestedBottomSheet
  } = useBottomSheet()
  const openSheet = options?.isRecommendationSheetSource ? openNestedBottomSheet : openBottomSheet
  const closeSheet = options?.isRecommendationSheetSource ? closeNestedBottomSheet : closeBottomSheet

  async function openPpfasGiftFund({
    isin,
    analyticsScreen
  }: {
    isin: string
    analyticsScreen: string
  }): Promise<boolean> {
    if (!isPpfasGiftFundIsin(isin)) {
      return false
    }

    showLoading()
    let sheet: ReactElement | null = null
    try {
      const result = await refetch()
      if (result.isError || result.data == null) {
        showToast({ message: PPFAS_GIFT_FUND_ERROR_TOAST, duration: 2500 })
        return true
      }
      const onboardingStatus = result?.data
      // Gift-city flow does not apply — caller continues the normal invest path
      if (onboardingStatus === GiftCityOnboardingStatus.NOT_APPLICABLE) {
        return false
      }
      // NOT_INITIATED sends the email request; SUCCESS opens WhatsApp
      if (onboardingStatus === GiftCityOnboardingStatus.NOT_INITIATED) {
        sheet = (
          <PpfasGiftFundNotOnboardedBottomSheet onDismiss={closeSheet} analyticsScreen={analyticsScreen} />
        )
      } else {
        sheet = (
          <PpfasGiftFundOnboardedBottomSheet onDismiss={closeSheet} analyticsScreen={analyticsScreen} />
        )
      }
    } catch {
      showToast({ message: PPFAS_GIFT_FUND_ERROR_TOAST, duration: 2500 })
      return true
    } finally {
      hideLoading()
    }

    try {
      if (sheet == null) {
        throw new Error('PPFAS gift fund sheet missing')
      }
      openSheet(sheet, closeSheet)
    } catch {
      showToast({ message: PPFAS_GIFT_FUND_ERROR_TOAST, duration: 2500 })
    }
    return true
  }

  return { openPpfasGiftFund }
}
